<script setup>
import { ref, reactive, computed, onMounted, inject, nextTick } from 'vue'
import { useFormulario } from '@/composables/useFormulario'
import { useAuthStore } from '@/stores/auth'
import { useMetadataListasStore } from '@/stores/metadataListas'
import FormularioCadastro from '@/components/base/formulario/FormularioCadastro.vue'
import CampoInput from '@/components/base/campos/CampoInput.vue'
import CampoSelect from '@/components/base/campos/CampoSelect.vue'
import CampoBusca from '@/components/base/campos/CampoBusca.vue'
import CampoBuscaLista from '@/components/base/campos/CampoBuscaLista.vue'
import ModalFormulario from '@/components/base/modal/ModalFormulario.vue'
import AuditLogViewer from '@/components/base/auditoria/AuditLogViewer.vue'
import { parceiroSearch } from '@/search-definitions/parceiroSearch'
import { LPCO_ST } from '@/constants/LPCO_ST'
import { criarConfigBuscaNcm } from '@/search-definitions/ncmSearch'

const props = defineProps({
  itemParaEditar: {
    type: Object,
    default: null
  },
  modeloVazio: {
    type: Object,
    default: () => ({
      idLpcoComex: 0,
      idOrganizacao: null,
      idFilial: null,
      numeroLPCO: '',
      lstOrgaoAnuente: '',
      lstModeloLPCO: '',
      lstTipoOperacaoComex: '',
      idParceiroProdutor: null,
      lstStatusLPCO: 'LPCO_ST1',
      dataRegistro: new Date().toISOString().split('T')[0],
      dataDeferimento: null,
      dataValidade: null,
      observacaoLivre: '',
      itens: []
    })
  },
  tipoOperacao: {
    type: String,
    default: ''
  }
})

const emit = defineEmits(['itemAtualizado', 'cancelar', 'show-notification'])

const authStore = useAuthStore()
const metadataStore = useMetadataListasStore()

const mostrarAuditoria = ref(false)
const abrirAuditoria = () => { mostrarAuditoria.value = true }

const isLoadingListas = ref(false)

const accordionState = reactive({
  identificacao: true,
  cicloVida: false,
  itens: true
})
const listas = reactive({})

const formErrors = inject('formErrors', ref({}))

const temPermissaoCompleta = computed(() =>
  authStore.isAdminInterno || (authStore.permissoes && authStore.permissoes.includes('MODULO_COMPLETO'))
)
const isReadOnly = computed(() => isEditing.value && !temPermissaoCompleta.value)

const {
  item,
  isLoading,
  isEditing,
  entityInfo,
  submitForm,
  handleCancel
} = useFormulario(props, emit)

const entityInfoCalculado = computed(() => {
  if (!isEditing.value) return entityInfo.value
  const partes = []
  if (item.value.numeroLPCO) partes.push(item.value.numeroLPCO)
  if (item.value.lstOrgaoAnuente) {
    const orgao = getOrgaoTexto(item.value.lstOrgaoAnuente)
    if (orgao) partes.push(orgao)
  }
  return {
    id: entityInfo.value?.id || item.value.idLpcoComex,
    name: partes.join(' - ') || 'Novo LPCO',
    nameLabel: 'LPCO'
  }
})

const configBuscaParceiroProdutor = computed(() => ({
  ...parceiroSearch,
  titulo: 'Selecionar Produtor/Fabricante',
  filtrosFixos: { idEmpresa: authStore.empresaAtiva, lstCategoria: 'PCAT2' }
}))

const configBuscaNcm = computed(() => criarConfigBuscaNcm())

const carregarListas = async () => {
  isLoadingListas.value = true
  try {
    const result = await metadataStore.carregarListasParaTabelas(['LpcoComex', 'LpcoComexItem'])
    Object.assign(listas, result)
  } catch (error) {
    emit('show-notification', { message: 'Erro ao carregar dados auxiliares.', type: 'error' })
  } finally {
    isLoadingListas.value = false
  }
}

const getOrgaoTexto = (id) => {
  const itemLista = (listas.LstOrgaoAnuente || []).find(s => s.idListaClienteItem === id)
  return itemLista ? itemLista.textoLista : id
}

const getUnidadeTexto = (id) => {
  const itemLista = (listas.LstUnidadeMedidaComex || []).find(s => s.idListaClienteItem === id)
  return itemLista ? itemLista.textoLista : id
}

// ============================================================
// MODAL DE ITENS DO LPCO
// ============================================================
const isModalItemOpen = ref(false)
const indiceItemEditando = ref(-1)

const novoItemModelo = {
  idNcm: null,
  codigoNcm: '',
  quantidadeAutorizada: null,
  lstUnidadeMedidaComex: '',
  valoresAtributos: []
}

const modalItem = reactive({ ...novoItemModelo })

const abrirModalItem = (index = -1) => {
  if (isReadOnly.value) return
  if (isItensLocked.value) return

  if (index === -1) {
    indiceItemEditando.value = -1
    Object.assign(modalItem, JSON.parse(JSON.stringify(novoItemModelo)))
  } else {
    indiceItemEditando.value = index
    const itemExistente = item.value.itens[index]
    Object.assign(modalItem, JSON.parse(JSON.stringify(itemExistente)))
  }

  isModalItemOpen.value = true
}

const salvarModalItem = () => {
  if (!modalItem.quantidadeAutorizada || modalItem.quantidadeAutorizada <= 0) {
    emit('show-notification', { message: 'Quantidade autorizada deve ser maior que zero.', type: 'warning' })
    return
  }
  if (!modalItem.lstUnidadeMedidaComex) {
    emit('show-notification', { message: 'Unidade de Medida é obrigatória.', type: 'warning' })
    return
  }

  const payload = {
    idNcm: modalItem.idNcm,
    codigoNcm: modalItem.codigoNcm,
    quantidadeAutorizada: modalItem.quantidadeAutorizada,
    lstUnidadeMedidaComex: modalItem.lstUnidadeMedidaComex,
    valoresAtributos: modalItem.valoresAtributos || []
  }

  if (indiceItemEditando.value === -1) {
    item.value.itens.push(payload)
  } else {
    item.value.itens.splice(indiceItemEditando.value, 1, payload)
  }

  isModalItemOpen.value = false
}

const removerItem = (index) => {
  item.value.itens.splice(index, 1)
}

const formatarNumeroView = (valor, decimais = 4) => {
  if (valor == null || valor === '') return '-'
  return new Intl.NumberFormat('pt-BR', {
    minimumFractionDigits: decimais,
    maximumFractionDigits: decimais
  }).format(Number(valor))
}

// ============================================================
// SUBMISSÃO
// ============================================================
const getStatusSeq = (status) => {
  const map = {
    [LPCO_ST.EmDigitacao]: 1,
    [LPCO_ST.ParaAnalise]: 2,
    [LPCO_ST.Deferido]: 3,
    [LPCO_ST.Indeferido]: 4,
    [LPCO_ST.Vencido]: 5
  }
  return map[status] || 0
}

const statusOriginal = ref(null)

const isItensLocked = computed(() => {
  if (!isEditing.value) return false
  return getStatusSeq(statusOriginal.value) >= 2
})

const camposBasicosLocked = computed(() => {
  if (!isEditing.value) return false
  return getStatusSeq(statusOriginal.value) >= 2
})

const camposCicloLocked = computed(() => {
  if (!isEditing.value) return false
  return getStatusSeq(statusOriginal.value) >= 3
})

const validarFormulario = () => {
  const erros = {}
  const camposComErro = []

  // Limpar erros anteriores
  formErrors.value = {}

  // Campos obrigatórios básicos (SEQ ≥ 1)
  if (!item.value.lstOrgaoAnuente) {
    erros.lstOrgaoAnuente = 'Órgão Anuente é obrigatório.'
    camposComErro.push('lstOrgaoAnuente')
  }
  if (!item.value.lstModeloLPCO) {
    erros.lstModeloLPCO = 'Modelo de LPCO é obrigatório.'
    camposComErro.push('lstModeloLPCO')
  }
  if (!item.value.lstTipoOperacaoComex) {
    erros.lstTipoOperacaoComex = 'Operação Comex é obrigatória.'
    camposComErro.push('lstTipoOperacaoComex')
  }
  if (!item.value.idParceiroProdutor) {
    erros.idParceiroProdutor = 'Produtor / Fabricante é obrigatório.'
    camposComErro.push('idParceiroProdutor')
  }

  // Campos obrigatórios conforme status (SEQ ≥ 2)
  const seq = getStatusSeq(item.value.lstStatusLPCO)
  if (seq >= 2) {
    if (!item.value.numeroLPCO) {
      erros.numeroLPCO = 'Nº LPCO (Protocolo) é obrigatório para este status.'
      camposComErro.push('numeroLPCO')
    }
    if (!item.value.dataRegistro) {
      erros.dataRegistro = 'Data de Registro é obrigatória para este status.'
      camposComErro.push('dataRegistro')
    }
  }
  if (seq >= 3) {
    if (!item.value.dataDeferimento) {
      erros.dataDeferimento = 'Data de Deferimento é obrigatória para este status.'
      camposComErro.push('dataDeferimento')
    }
    if (!item.value.dataValidade) {
      erros.dataValidade = 'Data de Validade é obrigatória para este status.'
      camposComErro.push('dataValidade')
    }
  }

  formErrors.value = erros

  if (camposComErro.length > 0) {
    // Notificar o usuário sobre os campos obrigatórios
    const mensagens = camposComErro.map(c => erros[c]).join('; ')
    emit('show-notification', { message: `Preencha os campos obrigatórios: ${mensagens}`, type: 'warning' })

    // Focar no primeiro campo com erro
    nextTick(() => {
      const el = document.getElementById(camposComErro[0])
      if (el) {
        el.focus()
        el.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }
    })
    return false
  }

  return true
}

const salvar = async () => {
  if (isReadOnly.value) return
  if (!validarFormulario()) return

  const payload = {
    ...item.value,
    idFilial: item.value.idFilial || (authStore.organizacaoAtiva ? Number(authStore.organizacaoAtiva) : null),
    itens: item.value.itens.map(i => ({
      idNcm: i.idNcm || null,
      codigoNcm: i.codigoNcm || null,
      quantidadeAutorizada: i.quantidadeAutorizada,
      lstUnidadeMedidaComex: i.lstUnidadeMedidaComex,
      valoresAtributos: (i.valoresAtributos || []).map(v => ({
        idAtributoNcm: v.idAtributoNcm,
        valorInformado: v.valorInformado
      }))
    }))
  }

  try {
    await submitForm('LpcoComex', 'idLpcoComex', payload)
  } catch (error) {
    const message = error?.response?.data?.message || 'Erro ao salvar LPCO.'
    emit('show-notification', { message, type: 'error' })
  }
}

const tipoOperacaoLocked = computed(() => {
  return !!props.tipoOperacao
})

onMounted(async () => {
  await carregarListas()
  if (isEditing.value && item.value.lstStatusLPCO) {
    statusOriginal.value = item.value.lstStatusLPCO
  }
  // Se recebeu tipoOperacao via prop, auto-preenche e bloqueia o campo
  if (props.tipoOperacao && !item.value.lstTipoOperacaoComex) {
    item.value.lstTipoOperacaoComex = props.tipoOperacao
  }
})
</script>

<template>
  <div class="lpco-cadastro-wrapper">
    <FormularioCadastro
      :module-name="'LPCO'"
      :is-loading="isLoading || isLoadingListas"
      :is-editing="isEditing"
      :entity-info="entityInfoCalculado"
      :desabilitar-salvar="isReadOnly"
      :accordion-state="accordionState"
      @submit="salvar"
      @cancelar="handleCancel"
    >
      <template #acoes-header v-if="isEditing">
        <button type="button" class="btn-icon-minimalista me-3" @click="abrirAuditoria" title="Ver Histórico de Alterações">
          <i class="bi bi-clock-history"></i>
        </button>
      </template>

      <div v-if="isReadOnly" class="alert alert-warning border-0 rounded-3 mb-4 d-flex align-items-center">
        <i class="bi bi-lock-fill fs-4 me-3 text-warning"></i>
        <div><strong>Modo de Leitura:</strong> O seu perfil não tem permissões para editar este registo.</div>
      </div>

      <!-- FRAME 1: IDENTIFICAÇÃO -->
      <div class="card mb-4">
        <div class="card-header bg-body-tertiary cursor-pointer d-flex justify-content-between align-items-center" @click="accordionState.identificacao = !accordionState.identificacao">
          <h5 class="mb-0"><i class="bi bi-file-earmark-text me-2 text-primary"></i> Identificação</h5>
          <i :class="['bi', accordionState.identificacao ? 'bi-chevron-up' : 'bi-chevron-down', 'text-secondary']"></i>
        </div>
        <div class="card-body" v-show="accordionState.identificacao">
          <div class="row g-3">
            <div class="col-md-4">
              <CampoBuscaLista
                id="lstOrgaoAnuente"
                label="Órgão Anuente"
                v-model="item.lstOrgaoAnuente"
                tabela="LpcoComex"
                required
                :disabled="isReadOnly || camposBasicosLocked"
              />
            </div>

            <div class="col-md-4">
              <CampoBuscaLista
                id="lstModeloLPCO"
                label="Modelo de LPCO"
                v-model="item.lstModeloLPCO"
                tabela="LpcoComex"
                required
                :disabled="isReadOnly || camposBasicosLocked"
              />
            </div>

            <div class="col-md-4">
              <CampoBuscaLista
                id="lstTipoOperacaoComex"
                label="Operação Comex"
                v-model="item.lstTipoOperacaoComex"
                tabela="LpcoComex"
                required
                :disabled="isReadOnly || camposBasicosLocked || tipoOperacaoLocked"
              />
            </div>

            <div class="col-md-6">
              <CampoBusca
                id="idParceiroProdutor"
                label="Produtor / Fabricante"
                v-model="item.idParceiroProdutor"
                :configuracao-busca="configBuscaParceiroProdutor"
                required
                :disabled="isReadOnly || camposBasicosLocked"
                @show-notification="p => emit('show-notification', p)"
              />
            </div>
          </div>
        </div>
      </div>

      <!-- FRAME 2: CICLO DE VIDA -->
      <div class="card mb-4">
        <div class="card-header bg-body-tertiary cursor-pointer d-flex justify-content-between align-items-center" @click="accordionState.cicloVida = !accordionState.cicloVida">
          <h5 class="mb-0"><i class="bi bi-calendar-check me-2 text-primary"></i> Ciclo de Vida</h5>
          <i :class="['bi', accordionState.cicloVida ? 'bi-chevron-up' : 'bi-chevron-down', 'text-secondary']"></i>
        </div>
        <div class="card-body" v-show="accordionState.cicloVida">
          <div class="row g-3">
            <div class="col-md-3">
              <CampoSelect
                id="lstStatusLPCO"
                label="Status"
                v-model="item.lstStatusLPCO"
                :options="listas.LstStatusLPCO || []"
                option-label="textoLista"
                option-value="idListaClienteItem"
                required
                :disabled="isReadOnly"
              />
            </div>

            <div class="col-md-3">
              <CampoInput
                id="numeroLPCO"
                label="Nº LPCO (Protocolo)"
                v-model="item.numeroLPCO"
                :disabled="isReadOnly || camposCicloLocked"
                descricao-informativa="Número de protocolo no Siscomex."
              />
            </div>

            <div class="col-md-3">
              <CampoInput
                id="dataRegistro"
                label="Data de Registro"
                type="date"
                v-model="item.dataRegistro"
                :disabled="isReadOnly || camposCicloLocked"
              />
            </div>

            <div class="col-md-3">
              <CampoInput
                id="dataDeferimento"
                label="Data de Deferimento"
                type="date"
                v-model="item.dataDeferimento"
                :disabled="isReadOnly"
              />
            </div>

            <div class="col-md-3">
              <CampoInput
                id="dataValidade"
                label="Data de Validade"
                type="date"
                v-model="item.dataValidade"
                :disabled="isReadOnly"
              />
            </div>

            <div class="col-md-12">
              <CampoInput
                id="observacaoLivre"
                label="Observações"
                type="textarea"
                v-model="item.observacaoLivre"
                :disabled="isReadOnly"
              />
            </div>
          </div>
        </div>
      </div>

      <!-- FRAME 3: ITENS DO LPCO -->
      <div class="card mb-4 border-primary">
        <div class="card-header bg-primary text-white cursor-pointer d-flex justify-content-between align-items-center" @click="accordionState.itens = !accordionState.itens">
          <h5 class="mb-0 text-white"><i class="bi bi-list-check me-2"></i> Produtos / Itens do LPCO</h5>
          <div class="d-flex align-items-center gap-3">
            <span class="badge bg-light text-primary fs-6">{{ item.itens.length }} {{ item.itens.length === 1 ? 'item' : 'itens' }}</span>
            <button type="button" class="btn btn-sm btn-light py-0" @click.stop="abrirModalItem(-1)" v-if="!isReadOnly && !isItensLocked">
              <i class="bi bi-plus-lg"></i>
            </button>
            <i :class="['bi', accordionState.itens ? 'bi-chevron-up text-white' : 'bi-chevron-down text-white']"></i>
          </div>
        </div>
        <div class="card-body border-top border-secondary-subtle p-0" v-show="accordionState.itens">
          <div class="admin-table-container border-0 shadow-none mb-0">
            <table class="table admin-table m-0">
              <thead>
                <tr>
                  <th>NCM</th>
                  <th class="text-end">Qtd Autorizada</th>
                  <th>Unidade</th>
                  <th class="text-end">Ações</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(linha, index) in item.itens" :key="index">
                  <td><code>{{ linha.codigoNcm || '-' }}</code></td>
                  <td class="text-end fw-bold">{{ formatarNumeroView(linha.quantidadeAutorizada) }}</td>
                  <td>{{ getUnidadeTexto(linha.lstUnidadeMedidaComex) }}</td>
                  <td class="text-end" v-if="!isReadOnly && !isItensLocked">
                    <button type="button" class="btn-icon text-primary" @click="abrirModalItem(index)"><i class="bi bi-pencil-fill"></i></button>
                    <button type="button" class="btn-icon text-danger" @click="removerItem(index)"><i class="bi bi-trash-fill"></i></button>
                  </td>
                </tr>
                <tr v-if="item.itens.length === 0">
                  <td colspan="4" class="text-center text-muted py-4">
                    Nenhum produto vinculado a este LPCO.
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

    </FormularioCadastro>

    <!-- MODAL DE ITEM DO LPCO -->
    <ModalFormulario
      v-model="isModalItemOpen"
      :titulo="indiceItemEditando === -1 ? 'Adicionar Produto ao LPCO' : 'Editar Produto do LPCO'"
      largura="modal-lg"
      textoConfirmar="Salvar"
      @confirmar="salvarModalItem"
    >
      <div class="row g-3">
        <div class="col-md-8">
          <CampoBusca
            id="idNcm"
            label="NCM"
            v-model="modalItem.idNcm"
            :configuracao-busca="configBuscaNcm"
            required
            :disabled="isReadOnly"
            @selecionado="val => { modalItem.idNcm = val.idNcm; modalItem.codigoNcm = val.codigoNcm; modalItem.lstUnidadeMedidaComex = val.lstUnidadeEstatistica }"
            @show-notification="p => emit('show-notification', p)"
          />
        </div>

        <div class="col-md-4">
          <CampoInput
            id="modalQuantidadeAutorizada"
            label="Quantidade Autorizada"
            type="moeda"
            :casasDecimais="4"
            v-model="modalItem.quantidadeAutorizada"
            required
            :disabled="isReadOnly"
          />
        </div>

        <div class="col-md-4">
          <CampoBuscaLista
            id="LstUnidadeMedidaComex"
            label="Unidade de Medida"
            v-model="modalItem.lstUnidadeMedidaComex"
            tabela="LpcoComexItem"
            required
            disabled
          />
        </div>
      </div>
    </ModalFormulario>

    <!-- AUDITORIA -->
    <AuditLogViewer
      v-if="mostrarAuditoria"
      tabela="LpcoComex"
      :chave-primaria="item.idLpcoComex"
      :titulo-registro="`LPCO ${item.idLpcoComex}`"
      contexto="tenant"
      :tabelas-filhas="['LpcoComexItem']"
      chave-estrangeira="IdLpcoComex"
      @fechar="mostrarAuditoria = false"
    />
  </div>
</template>

<style scoped>
.lpco-cadastro-wrapper {
  max-width: 100%;
  overflow-x: hidden;
}
</style>
